import React from "react";
import Skeleton from "react-loading-skeleton";
import { cardStyle, labelStyle } from "./hrUi";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <div>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#111827" }}>{title}</h4>
        {subtitle ? <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>{subtitle}</div> : null}
      </div>
      {actions ? <div className="d-flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

// Shows a skeleton while loading, the error (with retry) if it failed, else the children.
export function LoadState({ loading, error, onRetry, rows = 4, children }) {
  if (loading) {
    return (
      <div style={{ ...cardStyle, padding: 16 }} data-testid="hr-loading">
        <Skeleton count={rows} height={20} style={{ marginBottom: 10 }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...cardStyle, padding: 24, textAlign: "center" }} role="alert">
        <div style={{ color: "#991B1B", marginBottom: 12, fontSize: 14 }}>{error}</div>
        {onRetry ? (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }
  return children;
}

export function EmptyState({ title, text, action }) {
  return (
    <div style={{ ...cardStyle, padding: "40px 24px", textAlign: "center" }}>
      <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>{title}</div>
      {text ? <div style={{ fontSize: 13, color: "#6B7280", marginBottom: action ? 12 : 0 }}>{text}</div> : null}
      {action || null}
    </div>
  );
}

export function Field({ label, error, hint, required, children, style, htmlFor }) {
  return (
    <div className="mb-3" style={style}>
      {label ? (
        <label style={labelStyle} htmlFor={htmlFor}>
          {label}
          {required ? <span style={{ color: "#C9194A" }}> *</span> : null}
        </label>
      ) : null}
      {children}
      {hint && !error ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>{hint}</div> : null}
      {error ? (
        <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 4 }} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

// Simple tab strip; the parent owns which tab is active.
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="d-flex flex-wrap gap-1 mb-3" style={{ borderBottom: "1px solid #E5E7EB" }} role="tablist">
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.key)}
            style={{
              background: "none",
              border: "none",
              borderBottom: `2px solid ${on ? "#C9194A" : "transparent"}`,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: on ? 600 : 500,
              color: on ? "#C9194A" : "#4B5563",
              cursor: "pointer",
            }}
          >
            {t.label}
            {t.badge ? (
              <span style={{ marginLeft: 6, background: "#C9194A", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{t.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export const PrimaryButton = ({ children, ...rest }) => (
  <button type="button" className="btn Button primaryBackground text-white px-4" {...rest}>
    {children}
  </button>
);

export const SecondaryButton = ({ children, ...rest }) => (
  <button type="button" className="btn btn-outline-secondary px-4" {...rest}>
    {children}
  </button>
);

// A read-only label/value grid for profile-style details.
export function DetailGrid({ items, columns = 2 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: "14px 24px" }} className="hr-detail-grid">
      {items.map(([label, value]) => (
        <div key={label}>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 14, color: "#111827", overflowWrap: "anywhere" }}>{value === null || value === undefined || value === "" ? "—" : value}</div>
        </div>
      ))}
    </div>
  );
}
